import React, { useState, useEffect, useRef } from "react";
import "./Home.css";
import TopBar from "../components/TopBar";
import { translations } from "../i18n";

const PHONE = "+34 604 93 10 24";
const WHATSAPP_INTL = "34604931024";
const EMAIL = "transboxflow@gmail.com";

const HERO_IMAGES = ["/topone.png"];

const SERVICES = [
  {
    title: "Mudanzas de vivienda",
    desc: "Pisos, casas y trasteros.",
    image: "/image1.png",
  },
  {
    title: "Transporte de muebles",
    desc: "IKEA, Wallapop, sofás, armarios.",
    image: "/image2.png",
  },
  {
    title: "Embalaje y protección",
    desc: "Mantas, film, cantoneras y cartón.",
    image: "/image3.png",
  },
  {
    title: "Subida por escaleras",
    desc: "Montacargas opcional.",
    image: "/image4.png",
  },
  {
    title: "Desmontaje y montaje",
    desc: "Armarios, camas, mesas.",
    image: "/image5.png",
  },
  {
    title: "Servicio el mismo día",
    desc: "Según disponibilidad.",
    image: "/image6.png",
  },
];

const REVIEWS = [
  {
    name: "Noruega Idem",
    date: "14 Abril 2025",
    text: "Recientemente he realizado una mudanza con ellos, ya venía recomendada y realmente he quedado muy contenta.",
    initial: "N",
  },
  {
    name: "Esmeralda López",
    date: "7 Febrero 2025",
    text: "Estoy muy contenta con los servicios de esta empresa. Son rápidos, eficientes y muy cuidadosos.",
    initial: "E",
  },
  {
    name: "Tom Gorst",
    date: "20 Noviembre 2024",
    text: "Exceptional service moving the contents of our entire house from the UK to Barcelona, these guys are great.",
    initial: "T",
  },
  {
    name: "Julia Senosiain",
    date: "31 Octubre 2024",
    text: "Experiencia muy positiva, ha sido la mudanza perfecta. Rápidos y majísimos.",
    initial: "J",
  },
];

type LangCode = "es" | "en" | "fr" ;

const Home: React.FC = () => {
  const [name, setName] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [date, setDate] = useState("");
  const [currentLang, setCurrentLang] = useState<LangCode>("es");
  const [heroIndex, setHeroIndex] = useState(0);

  const reviewsRef = useRef<HTMLDivElement | null>(null);

  const scrollReviews = (direction: "left" | "right") => {
    const node = reviewsRef.current;
    if (!node) return;
    const amount = direction === "left" ? -320 : 320;
    node.scrollBy({ left: amount, behavior: "smooth" });
  };

  const t = translations[currentLang];

  useEffect(() => {
    if (HERO_IMAGES.length <= 1) return; // nothing to rotate

    const id = window.setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6000); // change every 6s

    return () => window.clearInterval(id);
  }, []);

  const nextHero = () => {
    setHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length);
  };

  const prevHero = () => {
    setHeroIndex((prev) => (prev - 1 + HERO_IMAGES.length) % HERO_IMAGES.length);
  };

  const goToHero = (index: number) => {
    setHeroIndex(index % HERO_IMAGES.length);
  };

  const whatsappHref = () => {
    const msg = encodeURIComponent(
      `Hola, soy ${name || "—"}. Quiero un presupuesto de mudanza:\nDesde: ${from || "—"}\nHasta: ${to || "—"}\nFecha: ${date || "—"}`
    );
    return `https://wa.me/${WHATSAPP_INTL}?text=${msg}`;
  };

  return (
    <>
      <TopBar currentLang={currentLang} onChangeLang={setCurrentLang} />

      {/* HERO image */}
      <section className="hero hero--image" aria-label="Imagen principal">
        <img
          key={heroIndex}
          className="hero__img"
          src={HERO_IMAGES[heroIndex]}
          alt="Mudanzas en Barcelona"
        />
        {HERO_IMAGES.length > 1 && (
          <>
            <button
              type="button"
              className="hero-nav hero-nav--prev"
              aria-label="Imagen anterior"
              onClick={prevHero}
            >
              ‹
            </button>
            <button
              type="button"
              className="hero-nav hero-nav--next"
              aria-label="Imagen siguiente"
              onClick={nextHero}
            >
              ›
            </button>
            <div className="hero-dots" aria-hidden="true">
              {HERO_IMAGES.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={
                    "hero-dot" + (index === heroIndex ? " hero-dot--active" : "")
                  }
                  onClick={() => goToHero(index)}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <main>
        {/* Headline + CTAs */}
        <section className="section section--tight">
          <div className="container">
            <h1 className="h1">{t.hero.title}</h1>
            <p className="lead">{t.hero.subtitle}</p>
            <div className="btnrow">
              <a href={`tel:${PHONE.replace(/\s+/g, "")}`} className="btn">
                {t.hero.callNow}
              </a>
              <a
                href={`https://wa.me/${WHATSAPP_INTL}?text=Hola,%20quiero%20un%20presupuesto%20de%20mudanza%20en%20Barcelona`}
                className="btn btn--ghost"
              >
                {t.hero.whatsapp}
              </a>
            </div>
          </div>
        </section>

        {/* Quick Quote */}
        <section className="section">
          <div className="container">
            <div className="card">
              <h2 className="h2">{t.quickQuote.title}</h2>
              <form
                className="formgrid"
                onSubmit={(e) => {
                  e.preventDefault();
                  window.open(whatsappHref(), "_blank");
                }}
              >
                <input
                  className="input"
                  placeholder={t.quickQuote.name}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <input
                  className="input"
                  placeholder={t.quickQuote.from}
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
                <input
                  className="input"
                  placeholder={t.quickQuote.to}
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
                <input
                  type="text"
                  className="input"
                  placeholder="Fecha (dd/mm/aaaa)"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
                <button className="btn" type="submit">
                  {t.quickQuote.sendWhatsApp}
                </button>
              </form>
              <p className="muted small">{t.quickQuote.helper}</p>
            </div>
          </div>
        </section>

        {/* Services */}
        <section id="servicios" className="section section--alt">
          <div className="container">
            <h2 className="h2">{t.services.title}</h2>
            <div className="grid3">
              {SERVICES.map(({ title, desc, image }) => (
                <div className="card" key={title}>
                  <img src={image} alt={title} className="service-icon" />
                  <h3 className="h3">{title}</h3>
                  <p className="muted">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team / trust strip above reviews */}
        <section className="section">
          <div className="container trust-section">
            {/* Left: small image */}
            <img
              src="/image.png"
              alt="Operario de BoxFlow sonriendo con una caja"
              className="trust-image"
            />

            {/* Right: text + stats */}
            <div className="trust-text">
              <h2 className="h2">Tu equipo de confianza para mudanzas</h2>
              <p className="muted">
                En BoxFlow trabajamos con un equipo fijo de profesionales de confianza.
                Nos encargamos de cargar, proteger y transportar tus cosas como si fueran
                nuestras, para que tu mudanza en Barcelona sea rápida y sin estrés.
              </p>

              <div className="stats-row">
                <div className="stat">
                  <div className="stat__number">1.200+</div>
                  <div className="stat__label">
                    clientes que ya se han mudado con nosotros
                  </div>
                </div>
                <div className="stat">
                  <div className="stat__number">4,9★</div>
                  <div className="stat__label">valoración media en reseñas</div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Reviews */}
        <section id="opiniones" className="section section--reviews">
          <div className="container">
            <h2 className="h2 reviews-title">Nuestros clientes</h2>

            <div className="reviews-layout">
              {/* Left: Google-style summary */}
              <div className="reviews-summary">
                <div className="reviews-summary-label">EXCELENTE</div>
                <div
                  className="reviews-summary-stars"
                  aria-label="5 de 5 estrellas"
                >
                  ★★★★★
                </div>
                <div className="reviews-summary-count">
                  A base de <strong>216 reseñas</strong>
                </div>
                <div className="reviews-summary-google">
                  <span className="google-text google-text--g">G</span>
                  <span className="google-text google-text--o1">o</span>
                  <span className="google-text google-text--o2">o</span>
                  <span className="google-text google-text--g">g</span>
                  <span className="google-text google-text--l">l</span>
                  <span className="google-text google-text--e">e</span>
                </div>
              </div>

              {/* Right: review cards slider */}
              <div className="reviews-slider">
                <button
                  type="button"
                  className="reviews-arrow reviews-arrow--left"
                  aria-label="Opiniones anteriores"
                  onClick={() => scrollReviews("left")}
                >
                  ‹
                </button>

                <div className="reviews-track" ref={reviewsRef}>
                  {REVIEWS.map((review) => (
                    <article className="review-card" key={review.name}>
                      <header className="review-card__header">
                        <div className="review-card__avatar">
                          {review.initial}
                        </div>
                        <div>
                          <div className="review-card__name-row">
                            <span className="review-card__name">
                              {review.name}
                            </span>
                            <span className="review-card__g">G</span>
                          </div>
                          <div className="review-card__date">
                            {review.date}
                          </div>
                        </div>
                      </header>

                      <div
                        className="review-card__stars"
                        aria-label="5 de 5 estrellas"
                      >
                        ★★★★★
                      </div>
                      <p className="review-card__text">{review.text}</p>
                      <button
                        type="button"
                        className="review-card__more"
                        aria-label="Leer reseña completa"
                      >
                        Leer más
                      </button>
                    </article>
                  ))}
                </div>

                <button
                  type="button"
                  className="reviews-arrow reviews-arrow--right"
                  aria-label="Opiniones siguientes"
                  onClick={() => scrollReviews("right")}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contacto" className="section">
          <div className="container grid2">
            <div>
              <h2 className="h2">{t.contact.title}</h2>
              <p className="muted">{t.contact.subtitle}</p>
              <ul className="contact">
                <li>
                  <strong>{t.contact.phone}</strong>{" "}
                  <a href={`tel:${PHONE.replace(/\s+/g, "")}`}>{PHONE}</a>
                </li>
                <li>
                  <strong>{t.contact.whatsapp}</strong>{" "}
                  <a href={`https://wa.me/${WHATSAPP_INTL}`}>wa.me/{WHATSAPP_INTL}</a>
                </li>
                <li>
                  <strong>{t.contact.email}</strong>{" "}
                  <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                </li>
              </ul>
            </div>
            <div className="card">
              <h3 className="h3">{t.contact.sendMediaTitle}</h3>
              <p className="muted">{t.contact.sendMediaText}</p>
              <a href={`https://wa.me/${WHATSAPP_INTL}`} className="btn">
                {t.contact.openWhatsApp}
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Home;
